import { useCallback } from 'react';
import OriginTracer from '@atlassiansox/origin-tracing';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { getSiteAri, useCurrentProduct } from '../../utils/index.tsx';
import { inviteWithinOrg } from './invite-users/index.tsx';
import type { BulkInviteSuccessResponse, InviteUsersRequest } from './types.tsx';
import { handleInviteUsersSuccessResponse, getOriginProduct } from './utils.tsx';

export const useInviteUsers = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const currentProduct = useCurrentProduct();

	return useCallback(
		async ({
			userIds,
			continueUrl,
			tenantId,
			product,
			integration,
		}: InviteUsersRequest): Promise<BulkInviteSuccessResponse> => {
			try {
				const origin = new OriginTracer({ product: getOriginProduct(product) });
				const continueUrlWithOrigin = origin.addToUrl(continueUrl);
				const originAttributes = origin.toAnalyticsAttributes({ hasGeneratedId: true });

				const inviteApiResponse = await inviteWithinOrg({
					userIds,
					continueUrl: continueUrlWithOrigin,
					tenantId,
					product,
					sourceProduct: currentProduct,
				});
				const response = handleInviteUsersSuccessResponse(inviteApiResponse);

				[...response.invited, ...response.accessRequested].forEach((invited) => {
					const resourceAri = getSiteAri(tenantId, product);
					const result = invited.results[resourceAri];
					const userId = invited.id;
					const analyticsAttributes = {
						...originAttributes,
						userId,
						products: [product],
						inviteResult: [
							{
								resourceAri,
								status: result,
							},
						],
						integration,
						isInviteWithinOrg: true,
					};
					if (result === 'INVITED') {
						fireTrackAnalytics(
							createAnalyticsEvent({}),
							'user invited',
							userId,
							analyticsAttributes,
						);
					} else if (result === 'INVITED_PENDING_APPROVAL') {
						fireTrackAnalytics(
							createAnalyticsEvent({}),
							'productAccess requested',
							userId,
							analyticsAttributes,
						);
					}
				});

				fireOperationalAnalytics(createAnalyticsEvent({}), 'promptOnLinkInvite succeeded', {
					invited: response.invited.length,
					requested: response.accessRequested.length,
					errored: response.error.length,
					integration,
				});

				return response;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInviteUsers',
						packageName: 'jiraInvitePromptOnLinkInsert',
					},
					error,
					attributes: {
						integration,
					},
				});
				throw error;
			}
		},
		[createAnalyticsEvent, currentProduct],
	);
};
